<template>
  <div id="app" :class="[themeStyle, theme]">
    <Page />
  </div>
</template>

<script>
import attempt from 'lodash/attempt';
import Page from '@/Page';

import { isUrlLang } from '@/utils/utils';

export default {
  name: 'App',
  created() {
    const currencyList = this.$root.$currencyList;
    const queryParamCurrencyId = this.$route.query.currency;
    const queryParamCurrency = currencyList.find(c => c.id === queryParamCurrencyId);
    const defaultCurrency = queryParamCurrency || currencyList.find(c => c.default === 1) || currencyList[0];

    this.$root.octobusCurrencyObj = this.parse(localStorage.getItem('__octobus__current-currency') || JSON.stringify(defaultCurrency));
    this.initializeSearchCurrency(queryParamCurrency, defaultCurrency);
  },
  components: {
    Page,
  },
  data() {
    return {
      lang: this.$framelocale,
    };
  },
  methods: {
    initializeSearchCurrency(queryParamCurrency, defaultCurrency) {
      let searchCurrency = null;
      if (queryParamCurrency) { // query parameter has higher priority
        searchCurrency = queryParamCurrency;
      } else {
        const searchCurrencyId = localStorage.getItem('__octobus__search-currency') || '';
        searchCurrency = this.$root.$currencyList.find(c => c.id === searchCurrencyId);
        if (!searchCurrency) {
          searchCurrency = defaultCurrency;
        }
      }
      localStorage.setItem('__octobus__search-currency', searchCurrency.id);
    },
    parse(str) {
      return attempt(JSON.parse.bind(null, str));
    },
    logout() {
      if (!this.isCheckoutRouteActive()) this.gotoMainPage();
    },
    isCheckoutRouteActive() {
      return this.$router.currentRoute && this.$router.currentRoute.name === 'Checkout';
    },
    gotoMainPage() {
      const lang = isUrlLang ? `/${this.$UrlLang}` : '';

      if (this.$framespa !== true) {
        location.href = `${lang}/`;
      } else {
        this.$router.push(`${lang}/`);
      }
    },
    profile() {
      const { children } = this.$router.options.routes[1];
      const route = children.find(r => r.name === 'Profile');
      const lang = isUrlLang ? `/${this.$UrlLang}` : '';

      if (this.$framespa !== true) {
        location.href = `${lang}/${route.path}`;
      } else {
        this.$router.push(`${lang}/${route.path}`);
      }
    },
    tickets() {
      const { children } = this.$router.options.routes[1];
      const route = children.find(r => r.name === 'Tickets');
      const lang = isUrlLang ? `/${this.$UrlLang}` : '';

      if (this.$framespa !== true) {
        location.href = `${lang}/${route.path}`;
      } else {
        this.$router.push(`${lang}/${route.path}`);
      }
    },
  },
  computed: {
    themeStyle() {
      return this.$frameparams.style;
    },
    isAuth() {
      return this.$frameauth;
    },
    theme() {
      return this.$theme;
    },
  },
  mounted() {
    this.$EventBus.$on('auth__logout', this.logout);
    this.$EventBus.$on('auth__profile', this.profile);
    this.$EventBus.$on('auth__tickets', this.tickets);
  },
};
</script>

<style lang="scss">
html {
  font-size: 100%;
}

label {
  font-weight: normal;
}

#app,
#auth {
  font-size: 1rem;

  .prepere-for-print {
    display: none;
  }

  .switch[type="checkbox"] + label {
    padding-top: 0;
  }

  .is-checkradio.is-danger[type="checkbox"] + label::before {
    border-color: #ff3860;
  }

  .octobus__input::-webkit-input-placeholder { /* Chrome/Opera/Safari */
    color: #4c4c4c !important;
  }
  .octobus__input::-moz-placeholder { /* Firefox 19+ */
    color: #4c4c4c !important;
  }
  .octobus__input:-ms-input-placeholder { /* IE 10+ */
    color: #4c4c4c !important;
  }
  .octobus__input:-moz-placeholder { /* Firefox 18- */
    color: #4c4c4c !important;
  }

  .is-checkradio[type="checkbox"] + label {
    margin-left: 0;
  }

  .is-checkradio[type="radio"] + label,
  .is-checkradio[type="checkbox"] + label {
    display: block;
    text-align: justify;
  }
}

/* spinner */
.light-green {
  color: #67b66b;
}

.light-blue {
  /* button */
  .octobus__button.is-primary[disabled],
  .octobus__button.is-primary {
    background: #39c !important;
  }

  /* avatar */
  .octobus__avatar {
    background: #39c;
  }

  /* nav menu */
  label.in-flow {
    color: #39c;
  }

  .octobus__nav {
    background: #39c!important;

    .nav-toggle.menu-hide {
      background: #fff;
      color: #39c;
      border: 1px solid #39c;
    }
  }

  /* input */
  .octobus__input.is-primary, .octobus__textarea.is-primary {
    border-color: #39c !important;

    &:active,
    &:focus {
      box-shadow: 0 0 0 0.125em rgba(24, 176, 249, 0.59) !important;
    }
  }

  .octobus__input.is-active,
  .octobus__input.is-focused,
  .octobus__input:active,
  .octobus__input:focus,
  .octobus__textarea.is-active,
  .octobus__textarea.is-focused,
  .octobus__textarea:active,
  .octobus__textarea:focus {
    border-color: #39c !important;
    box-shadow: 0 0 0 0.125em rgba(24, 176, 249, 0.59) !important;
  }

  /* select */
  .octobus__select.is-primary select {
    border-color: #39c !important;

    &:active,
    &:focus {
      box-shadow: 0 0 0 0.125em rgba(24, 176, 249, 0.59) !important;
    }
  }

  .is-checkradio[type="radio"] + label::after {
    background: #39c !important;
  }

  select.is-primary {
    border-color: #39c !important;
  }

  .octobus__select.is-primary:not(:hover)::after {
    border-color: #39c !important;
  }

  /* search: people*/
  .search .octobus__dropdown .people {
    border-color: #39c !important;
  }

  /* switcher */

  .switch[type="checkbox"].is-thin:checked + label::before {
    background: #39c !important;
  }

  .switch[type="checkbox"].is-outlined:checked + label::before {
    border-color: #39c !important;
  }

  .switch[type="checkbox"].is-outlined:checked + label::after {
    background: #39c !important;
  }

  /* calendar */
  .search .vdp-datepicker .vdp-datepicker__calendar .cell.selected,
  .search .vdp-datepicker .vdp-datepicker__calendar .cell.selected:hover {
    background: #39c !important;
    border-color: #39c !important;
  }

  .search .vdp-datepicker .vdp-datepicker__calendar .cell.today {
    border-color: #39c !important;
  }

  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover,
  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover,
  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover {
    border: 1px solid #39c !important;
  }

  .octobus__box {
    box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px #39c !important;
  }

  .route .station-block .station:before {
    border-color: #39c !important;
  }

  .is-divider {
    border-color: #39c !important;
  }

  .result .trips-menu .octobus__column .fa-sort-up.is-active,
  .result .trips-menu .octobus__column .fa-sort-down.is-active,
  .counter .fa-minus svg,
  .counter .fa-plus svg,
  .is-divider[data-content]::after,
  .is-divider-vertical[data-content]::after,
  .has-text-primary {
    color: #39c !important;
  }

  /* bus */
  .octobus__button.is-primary.is-outlined:hover,
  .octobus__button.is-primary.is-outlined:focus {
    background: transparent !important;
    border-color: #39c !important;
    color: #fff;
  }

  .octobus__button.is-primary.is-outlined {
    background: transparent !important;;
    border-color: #39c !important;
    color: #39c !important;
  }
}

.light-purple {
  /* button */
  .octobus__button.is-primary[disabled],
  .octobus__button.is-primary {
    background: #cc004d !important;
  }

  /* avatar */
  .octobus__avatar {
    background: #cc004d;
  }

  /* nav menu */
  label.in-flow {
    color: #cc004d;
  }

  .octobus__nav {
    background: #cc004d!important;

    .nav-toggle.menu-hide {
      background: #fff;
      color: #cc004d;
      border: 1px solid #cc004d;
    }
  }

  /* input */
  .octobus__input.is-primary, .octobus__textarea.is-primary {
    border-color: #cc004d !important;

    &:active,
    &:focus {
      box-shadow: 0 0 0 0.125em rgba(255, 56, 96, 0.25) !important;
    }
  }

  .octobus__input.is-active,
  .octobus__input.is-focused,
  .octobus__input:active,
  .octobus__input:focus,
  .octobus__textarea.is-active,
  .octobus__textarea.is-focused,
  .octobus__textarea:active,
  .octobus__textarea:focus {
    border-color: #cc004d !important;
    box-shadow: 0 0 0 0.125em rgba(255, 56, 96, 0.25) !important;
  }

  /* select */
  .octobus__select.is-primary select {
    border-color: #cc004d !important;

    &:active,
    &:focus {
      box-shadow: 0 0 0 0.125em rgba(255, 56, 96, 0.25) !important;
    }
  }

  .is-checkradio[type="radio"] + label::after {
    background: #cc004d !important;
  }

  select.is-primary {
    border-color: #cc004d !important;
  }

  .octobus__select.is-primary:not(:hover)::after {
    border-color: #cc004d !important;
  }

  /* search: people*/
  .search .octobus__dropdown .people {
    border-color: #cc004d !important;
  }

  /* switcher */
  .switch[type="checkbox"].is-thin:checked + label::before {
    background: #cc004d !important;
  }

  .switch[type="checkbox"].is-outlined:checked + label::before {
    border-color: #cc004d !important;
  }

  .switch[type="checkbox"].is-outlined:checked + label::after {
    background: #cc004d !important;
  }

  /* calendar */
  .search .vdp-datepicker .vdp-datepicker__calendar .cell.selected,
  .search .vdp-datepicker .vdp-datepicker__calendar .cell.selected:hover {
    background: #cc004d !important;
    border-color: #cc004d !important;
  }

  .search .vdp-datepicker .vdp-datepicker__calendar .cell.today {
    border-color: #cc004d !important;
  }

  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover,
  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover,
  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover {
    border: 1px solid #cc004d !important;
  }

  .octobus__box {
    box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px #cc004d !important;
  }

  .route .station-block .station:before {
    border-color: #cc004d !important;
  }

  .is-divider {
    border-color: #cc004d !important;
  }

  .result .trips-menu .octobus__column .fa-sort-up.is-active,
  .result .trips-menu .octobus__column .fa-sort-down.is-active,
  .counter .fa-minus svg,
  .counter .fa-plus svg,
  .is-divider[data-content]::after,
  .is-divider-vertical[data-content]::after,
  .has-text-primary {
    color: #cc004d !important;
  }

    /* bus */
  .octobus__button.is-primary.is-outlined:hover,
  .octobus__button.is-primary.is-outlined:focus {
    background: transparent !important;
    border-color: #cc004d !important;
    color: #fff;
  }

  .octobus__button.is-primary.is-outlined {
    background: transparent !important;
    border-color: #cc004d !important;
    color: #cc004d !important;
  }
}

.light-orange {
    /* button */
  .octobus__button.is-primary[disabled],
  .octobus__button.is-primary {
    background: #f90 !important;
  }

  /* avatar */
  .octobus__avatar {
    background: #f90;
  }

  /* nav menu */
  label.in-flow {
    color: #f90;
  }

  .octobus__nav {
    background: #f90!important;

    .nav-toggle.menu-hide {
      background: #fff;
      color: #f90;
      border: 1px solid #f90;
    }
  }

  /* input */
  .octobus__input.is-primary, .octobus__textarea.is-primary {
    border-color: #f90 !important;

    &:active,
    &:focus {
      box-shadow: 0 0 0 0.125em rgba(255, 202, 53, 0.52) !important;
    }
  }

  .octobus__input.is-active,
  .octobus__input.is-focused,
  .octobus__input:active,
  .octobus__input:focus,
  .octobus__textarea.is-active,
  .octobus__textarea.is-focused,
  .octobus__textarea:active,
  .octobus__textarea:focus {
    border-color: #f90 !important;
    box-shadow: 0 0 0 0.125em rgba(255, 202, 53, 0.52) !important;
  }

  /* select */
  .octobus__select.is-primary select {
    border-color: #f90 !important;

    &:active,
    &:focus {
      box-shadow: 0 0 0 0.125em rgba(255, 202, 53, 0.52) !important;
    }
  }

  .is-checkradio[type="radio"] + label::after {
    background: #f90 !important;
  }

  select.is-primary {
    border-color: #f90 !important;
  }

  .octobus__select.is-primary:not(:hover)::after {
    border-color: #f90 !important;
  }

  /* search: people*/
  .search .octobus__dropdown .people {
    border-color: #f90 !important;
  }

  /* switcher */
  .switch[type="checkbox"].is-thin:checked + label::before {
    background: #f90 !important;
  }

  .switch[type="checkbox"].is-outlined:checked + label::before {
    border-color: #f90 !important;
  }

  .switch[type="checkbox"].is-outlined:checked + label::after {
    background: #f90 !important;
  }

  /* calendar */
  .search .vdp-datepicker .vdp-datepicker__calendar .cell.selected,
  .search .vdp-datepicker .vdp-datepicker__calendar .cell.selected:hover {
    background: #f90 !important;
    border-color: #f90 !important;
  }

  .search .vdp-datepicker .vdp-datepicker__calendar .cell.today {
    border-color: #f90 !important;
  }

  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover,
  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover,
  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover {
    border: 1px solid #f90 !important;
  }

  .octobus__box {
    box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px #f90 !important;
  }

  .route .station-block .station:before {
    border-color: #f90 !important;
  }

  .is-divider {
    border-color: #f90 !important;
  }

  .result .trips-menu .octobus__column .fa-sort-up.is-active,
  .result .trips-menu .octobus__column .fa-sort-down.is-active,
  .counter .fa-minus svg,
  .counter .fa-plus svg,
  .is-divider[data-content]::after,
  .is-divider-vertical[data-content]::after,
  .has-text-primary {
    color: #f90 !important;
  }

  /* bus */
  .octobus__button.is-primary.is-outlined:hover,
  .octobus__button.is-primary.is-outlined:focus {
    background: transparent !important;
    border-color: #f90 !important;
    color: #fff;
  }

  .octobus__button.is-primary.is-outlined {
    background: transparent !important;
    border-color: #f90 !important;
    color: #f90 !important;
  }
}

.dark-green {
  color: green;
}


.dark-blue {
  color: blue;
}

.dark-purple {
  color: purple;
}

.dark-orange {
  color: orange;
}

.is-loading {
  position: relative;
  pointer-events: none;
  opacity: 0.5;
  &:after {
    -webkit-animation: spinAround 500ms infinite linear;
    animation: spinAround 500ms infinite linear;
    border: 2px solid #dbdbdb;
    border-radius: 290486px;
    border-right-color: transparent;
    border-top-color: transparent;
    content: "";
    display: block;
    height: 1em;
    position: relative;
    width: 1em;
    position: absolute;
    top: calc(50% - 2.5em);
    left: calc(50% - 2.5em);
    width: 5em;
    height: 5em;
    border-width: 0.25em;
  }
}

@media only screen and (max-width : 768px) {
  section.octobus__section {
    padding: 2rem 1rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue